import { useCallback, useState } from 'react';
import type { FC } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Alert, AppBar, Box, Button, Divider, IconButton, Toolbar, Tooltip } from '@material-ui/core';
import type { Theme } from '@material-ui/core';
import { ChevronDown as ChevronDownIcon } from '../icons/chevron-down';
import { Duplicate as DuplicateIcon } from '../icons/duplicate';
import { AccountPopover } from './account-popover';
import { TenantPopover } from './tenants-popover';
import { Logo } from './logo';
import { DashboardNavbarMenu } from './dashboard-navbar-menu';
import { useAuth } from '../hooks/use-auth';
import { SUPER_ADMIN_TENANT } from '../types/tenant';
import { storage } from '../utils/storage';

export const DashboardNavbar: FC = () => {
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { user: currentUser, tenant: currentTenant, tenants, setTenant } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(false);

  const handleCopyTenantTitle = useCallback(() => {
    if (!currentTenant?.title) return;
    navigator.clipboard
      .writeText(currentTenant.title)
      .then(() => toast.success('Tenant name copied'))
      .catch(() => toast.error('Could not copy tenant name'));
  }, [currentTenant?.title]);

  const handleTenantChange = (tenantId: string): void => {
    const newTenant = tenants.find((ten) => ten.id === tenantId);

    if (newTenant?.id !== currentTenant.id) {
      toast.success('Tenant changed');

      // Reset page controllers (filters, query, sort, ...)
      storage.controllers.remove();

      // From/To Super Admin
      if (newTenant.id === SUPER_ADMIN_TENANT.id || currentTenant.id === SUPER_ADMIN_TENANT.id) {
        setTenant(newTenant);
        navigate('/');
      }
      // Tenants Path
      else if (location.pathname.startsWith('/tenants/')) {
        // Tenants path in format: /tenants/:tenantId/...
        const [tenantPath] = location.pathname.split('/').slice(3);
        navigate(`/tenants/${newTenant.id}/${tenantPath}`);
      }
      // Other Paths
      else {
        setTenant(newTenant);
      }
    }
  };

  return (
    <AppBar elevation={0} sx={{ backgroundColor: '#1e212a' }}>
      <Toolbar
        disableGutters
        sx={{
          alignItems: 'center',
          display: 'flex',
          minHeight: 64,
          px: 3,
          py: 1,
        }}
      >
        <Box
          component={RouterLink}
          to="/"
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Logo variant="light" emblemOnly />
        </Box>
        <Divider
          flexItem
          orientation="vertical"
          sx={{
            borderColor: 'rgba(255,255,255,0.1)',
            mx: 3,
          }}
        />
        {currentTenant && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                '&:hover .navbar-copy-tenant-btn': { opacity: 1 },
                '&:focus-within .navbar-copy-tenant-btn': { opacity: 1 },
              }}
            >
              <TenantPopover
                currentTenant={currentTenant}
                onTenantChange={handleTenantChange}
                tenants={tenants}
              />
              <Tooltip title="Copy tenant name" placement="bottom">
                <IconButton
                  className="navbar-copy-tenant-btn"
                  size="small"
                  aria-label="Copy tenant name"
                  onClick={(event) => {
                    handleCopyTenantTitle();
                    // Drop focus so the hover-revealed icon hides once the
                    // pointer leaves; keyboard users still see it via
                    // :focus-within when they tab in.
                    event.currentTarget.blur();
                  }}
                  sx={{
                    ml: 0.5,
                    color: 'primary.contrastText',
                    opacity: 0,
                    transition: 'opacity 150ms ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  <DuplicateIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {!currentTenant.isActive && (
              <Alert
                severity="warning"
                sx={{
                  ml: 2,
                  py: 0.5,
                }}
                variant="filled"
              >
                Tenant is deactivated, please select another or contact support
              </Alert>
            )}
          </>
        )}
        <DashboardNavbarMenu onClose={() => setOpenMenu(false)} open={mdDown && openMenu} />
        <Button
          endIcon={
            <ChevronDownIcon
              fontSize="small"
              sx={{
                ml: 2,
                transition: 'transform 250ms',
                transform: openMenu ? 'rotate(180deg)' : 'none',
              }}
            />
          }
          onClick={() => setOpenMenu(true)}
          sx={{
            color: 'primary.contrastText',
            display: {
              md: 'none',
              xs: 'flex',
            },
          }}
          variant="text"
        >
          Menu
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <AccountPopover currentUser={currentUser} />
      </Toolbar>
    </AppBar>
  );
};
